<template>
  <div id="dispatch-caller-toast" class="toast hide fade" role="alert" aria-live="assertive" aria-atomic="true">
    <div class="toast-header">
      <i class="toast-icons fa-solid fa-phone-volume"></i>
      {{ currentState }}
    </div>
    <div class="toast-body">

      <span>
        {{ currentState }} from {{ caller }}
      </span>


      <p class="text-danger" v-if="currentState === STATES.CALL_IN_PROGRESS && muted">
        Your microphone is muted.
      </p>


      <div class="mt-2 pt-2 border-top d-flex justify-content-between"
           v-if="currentState === STATES.INCOMING_CALL || currentState === STATES.CALL_IN_PROGRESS">
        <div v-if="currentState === STATES.INCOMING_CALL">
          <a class="btn btn-pill btn-success align-items-center pull-left" @click="acceptCall">
            <i class="fa-solid fa-check"></i>
            Accept
          </a>
        </div>

        <div v-if="currentState === STATES.INCOMING_CALL">
          <a class="btn btn-pill btn-danger align-items-center pull-right" @click="rejectCall">
            <i class="fa-solid fa-ban"></i>
            Decline
          </a>
        </div>


        <div v-if="currentState === STATES.CALL_IN_PROGRESS">
          <a class="btn btn-pill align-items-center btn-outline-primary"
             :title="(muted) ? 'Unmute microphone' : 'Mute microphone'"
             @click="muteClick">

            <span v-if="muted">
              <i class="fa-solid fa-microphone"></i>
              Unmute
            </span>
            <span v-else>
              <i class="fa-solid fa-microphone-slash"></i>
              Mute
            </span>
          </a>
        </div>

        <div v-if="currentState === STATES.CALL_IN_PROGRESS">
          <a class="btn btn-pill btn-danger align-items-center" @click="endCall">
            <i class="fa-solid fa-ban"></i>
            End Call
          </a>
        </div>
      </div>
    </div>
  </div>


  <div id="call-summary-toast" class="toast hide fade" role="alert" aria-live="assertive" aria-atomic="true">
    <div class="toast-header">
      <i class="fa-solid fa-list-check"></i>
      <strong class="me-auto">Call summary</strong>
      <button class="btn-close" type="button" data-bs-dismiss="toast" aria-label="Close"></button>
    </div>

    <div class="toast-body">
      <div class="mt-2">
        <span class="b f-w-300">
          <i class="text-warning fa-solid fa-circle-exclamation"></i>
          This summary is AI generated and may not be accurate.
          Double check before booking.
        </span>
      </div>

      <div class="row">
        <div class="col-12 f-w-700">
          Pick-up:
          <span v-if="pickUpAddressSuggestions.length === 0" class="f-w-400" title="Summary not available">
            N/A
          </span>

          <ul class="list-group" v-for="pickUpAddressSuggestion in pickUpAddressSuggestions">
            <li class="list-group-item border-left-primary">
              <span class="f-w-400"
                    v-html="pickUpAddressSuggestion.label"
                    style="width: 250px; text-overflow: ellipsis;white-space: nowrap; overflow: hidden;">
              </span>
              <a class="btn btn-link p-0" @click="usePickUpSuggestion(pickUpAddressSuggestion)"
                 title="Use this suggestion to book.">Use this.</a>
            </li>
          </ul>
        </div>
        <div class="col-12 f-w-700 mt-2">
          Drop-off:
          <span v-if="dropOffAddressSuggestions.length === 0" class="f-w-400" title="Summary not available">
            N/A
          </span>

          <ul class="list-group" v-for="dropOffAddressSuggestion in dropOffAddressSuggestions">
            <li class="list-group-item border-left-secondary">
              <span class="f-w-400"
                    v-html="dropOffAddressSuggestion.label"
                    style="width: 250px; text-overflow: ellipsis;white-space: nowrap; overflow: hidden;">

              </span>
              <a class="btn btn-link p-0" @click="useDropOffSuggestion(dropOffAddressSuggestion)"
                 title="Use this suggestion to book.">Use this.</a>
            </li>
          </ul>
        </div>
      </div>

      <div class="mt-2 pt-2 border-top">
        <a class="btn btn-pill btn-primary align-items-end" @click="dismissCallSummary">
          <i class="fa-solid fa-check"></i>
          Dismiss
        </a>
      </div>

    </div>
  </div>


</template>

<script setup>
import {onMounted, ref} from "vue";
import {Device} from "@twilio/voice-sdk";
import {Toast} from 'bootstrap';
import {showToast} from "../utility/toastNotificationUtility";
import {eventBus} from '../utility/eventBus';
import {renderWithPoi} from "../utility/addressHelper";

//Call states
const STATES = ref({
  INCOMING_CALL: 'Incoming Call',
  CALL_REJECTED: 'Call Rejected',
  CALL_ENDED: 'Call Ended',
  CALL_IN_PROGRESS: 'Call In-Progress'
});


// Reactive state variables
let device = undefined;
let call = undefined;
let dispatcherCallToast;
let callSummaryToast;

const currentState = ref(STATES.value.INCOMING_CALL);
const caller = ref("");
const muted = ref(false);
const dropOffAddressSuggestions = ref([]);
const pickUpAddressSuggestions = ref([]);

// Initialize Twilio Device
const initializeDevice = async () => {
  try {
    // Fetch Twilio token from the backend
    const token = await getDeviceToken();
    // Initialize the Twilio Device
    device = new Device(token, {debug: true});
    device.audio.incoming(true);

    device.on("error", (error) => {
      console.error("Twilio Device Error:", error);
    });

    device.on("tokenWillExpire", async () => {
      console.log('token expiring');
      //update the device token, if there is a call in session, there may be a loss in audio, but it is acceptable
      const token = await getDeviceToken();
      device.updateToken(token);
    });

    // Handle incoming calls
    device.on("incoming", (incomingCall) => {
      console.log("incoming");
      call = incomingCall;
      currentState.value = STATES.value.INCOMING_CALL;
      caller.value = call.parameters.From || "Unknown Caller";

      call.on("disconnect", () => {
        endCall();
      });

      dispatcherCallToast.show();
    });

    await device.register();

    console.log("Twilio Device initialized");
  } catch (error) {
    console.error("Failed to initialize Twilio Device:", error);
  }
};

// Accept the call
const acceptCall = () => {
  if (call) {
    call.accept();
    currentState.value = STATES.value.CALL_IN_PROGRESS;
    console.log("Call accepted");

    //set the dispatcher on the call
    setDispatcherOnCall(call);
  }
};

// Reject the call
const rejectCall = () => {
  if (call) {
    call.reject();
    currentState.value = STATES.value.CALL_REJECTED;
    console.log("Call rejected");
    caller.value = "";

    //set the dispatcher on the call
    setDispatcherOnCall(call);

    //hide toast
    setTimeout(() => {
      dispatcherCallToast.hide();
    }, 3000);
    call = undefined;
  }
};

// endCall
const endCall = () => {
  if (call) {
    call.disconnect();
    currentState.value = STATES.value.CALL_ENDED;

    //hide toast
    setTimeout(() => {
      dispatcherCallToast.hide();
    }, 3000);

    //get call summary
    showToast({body: "Call summary is being generated.."});
    pollForCallSummary(call);
    eventBus.emit('emitCallerPhoneNumber', caller.value);
    call = undefined;
    console.log("Call ended");
  }
};

const muteClick = () => {
  if (call) {
    if (call.isMuted()) {
      call.mute();
      muted.value = false;
    } else {
      call.mute();
      muted.value = true;
    }
  }
}

const pollForCallSummary = (call) => {
  const params = new URLSearchParams(call.parameters.Params);
  const callFrom = call.parameters.From;

// Get the value of "ParentCallSid"
  const parentCallSid = params.get("ParentCallSid");

  const pollInterval = 1000; // Poll every second
  const maxAttempts = 5;   // Stop after 5 attempts (about 5 seconds)
  let attempts = 0;

  const poll = async () => {
    try {
      const response = await fetch(`/api/call/get-summary?callSid=${parentCallSid}`);

      if (response.status === 200) {
        const callSummary = await response.json();
        pickUpAddressSuggestions.value = await getValidAddress(callSummary.pickupAddress);
        dropOffAddressSuggestions.value = await getValidAddress(callSummary.dropOffAddress);

        callSummaryToast.show();

        return; // Stop polling on success
      } else {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval); // Schedule the next poll
        } else {
          //show message for unsuccessful summary generation
          showToast({
            title: "Summary Error",
            body: `Unable to generate AI summary for call from ${callFrom}`,
            type: 'warn'
          })
        }
      }
    } catch (error) {
      console.error("Error polling call summary:", error);
    }

  }
  poll();
}

const dismissCallSummary = () => {
  callSummaryToast.hide();
}

const usePickUpSuggestion = (value) => {
  //redirect to dispatch page if necessary
  eventBus.emit('emitPickupSuggestionToForm', value);

}

const useDropOffSuggestion = (value) => {
  //redirect to dispatch page if necessary
  eventBus.emit('emitDropOffSuggestionToForm', value);
}


const getValidAddress = async (suggestedAddress) => {
  try {
    if (suggestedAddress) {
      const response = await fetch(`/api/dispatch-ride/autocomplete?query=${encodeURIComponent(suggestedAddress)}`);

      if (response.ok) {
        const resData = await response.json();
        if (resData.results instanceof Array) {
          return resData.results.map((res) => {
            return {result: res, label: renderWithPoi(res), value: res.address.freeformAddress};
          }).slice(0, 2);
        }
      }
    }
  } catch (err) {
    console.log('error checking validity', err);
  }

  return [];
}

const getDeviceToken = async () => {
  const response = await fetch("/api/integration/twilio/token");
  if (!response.ok) throw new Error("Failed to fetch Twilio token");
  return await response.text();
}

const setDispatcherOnCall = (callObj) => {
  const params = new URLSearchParams(callObj.parameters.Params);
  const parentCallSid = params.get("ParentCallSid");
  //async not necessary
  fetch(`/api/call/set-dispatcher?callSid=${parentCallSid}`)
}

// Lifecycle hook
onMounted(() => {
  let toastContainer = $(document.getElementById("global-toast-container"));

  //Set up toasts (call and call summary)
  dispatcherCallToast = new Toast($('#dispatch-caller-toast'), {autohide: false});
  callSummaryToast = new Toast($('#call-summary-toast'), {autohide: false});

  toastContainer.prepend($('#dispatch-caller-toast'));
  toastContainer.prepend($('#call-summary-toast'));

  //initialize device
  initializeDevice();
});
</script>